import React, { useCallback, useEffect, useState } from "react";
import { Toolbar, Typography, Box, Button, Tooltip } from "@mui/material";
import TableRowsLoader from "../../../components/common/Loader/tableLoader";
import BulkChecksTable from "../../../components/bulkChecksTable/BulkChecksTable";
import { CheckType } from "../../../types/genericType";
import { getCheckDetails } from "../../../services/batchService";
import PrintIcon from "@mui/icons-material/Print";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { apiEndPoints } from "../../../variables/apiEndpoints";
import useFileDownloader from "../../../hooks/useFileDownloader";
import CustomSnackbar from "../../../components/common/Snackbar/CustomSnackbar";
import { LoadingButton } from "@mui/lab";

const BulkChecks = () => {
  const [search, setSearch] = useState("");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [checkDetails, setCheckDetails] = useState<CheckType[]>([]);
  const { downloadFile } = useFileDownloader();
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const initialLoad = () => {
    setIsLoading(true);
    getCheckDetails().then((res) => {
      setIsLoading(false);
      setCheckDetails(res);
    });
  };

  useEffect(() => {
    initialLoad();
  }, []);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearch(event.target.value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const filteredChecks = checkDetails.filter((check: any) => {
    return check.payee.toLowerCase().includes(search.toLowerCase());
  });

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    const result = await downloadFile({
      url: `/reports/${apiEndPoints.check}`,
      method: "post",
      fileName: "CheckRecords.pdf",
      data: rowSelectionModel,
    });
    setIsLoading(false);
    if (result.success) {
      setRowSelectionModel([]);
      initialLoad();
    } else {
      setSnackbarMsg(result.errorMessage || "Error downloading file");
      setOnSuccess(true);
    }
  }, [downloadFile]);

  const checkCheckNumbers = () => {
    for (const entry of checkDetails) {
      if (rowSelectionModel.includes(entry.id)) {
        if (entry.number === null) {
          return false;
        }
      }
    }
    return true;
  };

  const handlePrintChecks = () => {
    if (rowSelectionModel.length > 0 && checkCheckNumbers()) {
      handleDownload();
    } else {
      setSnackbarMsg("Please update check numbers");
      setOnSuccess(true);
    }
  };

  useEffect(() => {
    setIsDisabled(!checkCheckNumbers() || rowSelectionModel.length === 0);
  }, [rowSelectionModel]);

  return (
    <>
      <Toolbar
        sx={{
          justifyContent: "space-between",
          margin: "0px 15px",
        }}
      >
        <Typography className="transactions-history-title">
          Batch Checks
        </Typography>
        {isDisabled ? (
          <Tooltip
            title="Please select checks and ensure all have check numbers"
            placement="bottom-start"
            arrow
            enterDelay={1000}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                className="new-btn"
                component="button"
                onClick={handlePrintChecks}
                disabled={isDisabled}
              >
                {<PrintIcon sx={{ marginRight: "10px" }} />} PRINT
              </Button>
            </span>
          </Tooltip>
        ) : (
          <LoadingButton
            variant="contained"
            color="primary"
            className="new-btn"
            component="button"
            onClick={handlePrintChecks}
            disabled={isDisabled}
            loading={isLoading}
          >
            {<PrintIcon sx={{ marginRight: "10px" }} />} PRINT
          </LoadingButton>
        )}
      </Toolbar>
      <Box sx={{ margin: "20px 40px" }}>
        {!isLoading ? (
          checkDetails.length > 0 ? (
            <BulkChecksTable
              initialLoad={initialLoad}
              data={filteredChecks}
              search={search}
              onSearchChange={handleSearchChange}
              clearSearch={clearSearch}
              rowSelectionModel={rowSelectionModel}
              setRowSelectionModel={setRowSelectionModel}
            />
          ) : (
            <Typography>No Check records found</Typography>
          )
        ) : (
          <TableRowsLoader rowsNum={10} />
        )}
      </Box>
      <CustomSnackbar
        open={onSuccess}
        message={snackbarMsg}
        setOpen={setOnSuccess}
      />
    </>
  );
};

export default BulkChecks;
